/*
  This component adds the styles for “annotations”, the highlighted lines in the code snippet (and, in the future, highlighted words?). I created a separate component because `CodeWrapper` is already so complex. But essentially we’ll merge the two styled components with composition.
*/
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';

import {
  BaseWrapper as BaseAside,
  InfoAside,
  SuccessAside,
  WarningAside,
} from '@/components/Aside';

const AnnotatedContainer = styled.div`
  --bump-width: 3px;
  --default-inline-padding: var(--viewport-padding);
  --inline-padding: var(
    --inline-padding-override,
    var(--default-inline-padding)
  );

  /*
    On smaller screens, we add horizontal scroll to view longer lines. Highlighted lines should be highlighted for their *full length*; the highlighted background color shouldn't scroll out of view.

    To accomplish this, we make sure the '<pre>', which sits just inside the scroll container, grows to contain its largest child.
  */
  pre {
    width: fit-content;
    min-width: 100%;
  }

  code {
    /*
      HACK: For some reason, setting “display: block” on each .line adds a bunch of mysterious space between each line. A Flex column fixes it. At some point I should figure this out.
    */
    display: flex;
    flex-direction: column;
  }

  .line {
    display: block;
    /*
      We need to establish a min height for empty rows, to make sure they don't collapse under the Flex column.
    */
    min-height: var(--code-line-height, 1.625rem);
    /*
      Weirdly, iOS Safari sometimes cranks up the font-size on certain snippets. Disable this behaviour:
    */
    -webkit-text-size-adjust: 100%;

    /*
      The code snippet, so far, has no inline padding.
      This curious structure is so that highlighted lines (styled below) automatically fill the horizontal space.
      See above for how --inline-padding is calculated.
    */
    padding-inline: var(--inline-padding);

    @media ${BREAKPOINTS.mdAndLarger} {
      ${BaseAside} & {
        --default-inline-padding: calc(var(--inline-padding) * 0.5);
      }
    }
  }

  /*
    When Shiki runs the code, it creates a <span> for each line, with the class "line". I have a custom transformer that adds a second class, "highlighted", to the annotated lines.

    Triple .highlighted to make sure these styles take precedence over the ones set in CodeWrapper, which set the styles for Shiki classes.
  */
  .line.highlighted.highlighted.highlighted {
    position: relative;
    display: block;

    html[data-color-mode='light'] & {
      background: var(--color-info-200);
    }

    html[data-color-mode='dark'] & {
      /*
        In Dark Mode, the snippets have outlines, and I don't want the highlighted bump to overlap with the outline.
      */
      margin-left: 1px;
      margin-right: 1px;

      background: var(--color-gray-100);
    }

    /* Inside the Asides, however, there is no outline on the code snippets, so I *don't* need to become inset by 1px. */
    html[data-color-mode='dark'] ${BaseAside} & {
      margin-left: 0;
      margin-right: 0;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: var(--bump-width);
      height: 100%;
      background: var(--color-info-400);

      html[data-color-mode='dark'] & {
        background-color: var(--color-gray-300);
      }
    }

    ${InfoAside} & {
      background: var(--color-info-300);

      &::before {
      }

      html[data-color-mode='dark'] & {
        background: var(--color-info-300);

        &::before {
          background-color: var(--color-info-700);
          opacity: 0.5;
        }
      }
    }
    ${WarningAside} & {
      background: var(--color-warning-300);

      &::before {
        background-color: var(--color-warning-500);
        opacity: 0.5;
      }

      html[data-color-mode='dark'] & {
        background: var(--color-warning-300);

        &::before {
          background-color: var(--color-warning-500);
        }
      }
    }
    ${SuccessAside} & {
      background: var(--color-success-300);

      &::before {
        background-color: var(--color-success-500);
        opacity: 0.5;
      }

      html[data-color-mode='dark'] & {
        background: var(--color-success-300);

        &::before {
          background-color: var(--color-success-500);
        }
      }
    }

    /*
      Demo units are “dark mode” themed, BUT we typically render code snippets with a lighter background. We want to keep the “light mode” annotation styles.
    */
    html[data-color-mode='dark'] [data-demo-unit='true'] & {
      background: var(--color-info-200);

      &::before {
        background: var(--color-info-400);
      }
    }
  }

  /*
    Alright, so the way our Shiki transformation works, we add a ".highlighted" class to each highlighted line:

    <span class="line">...</span>
    <span class="line highlighted">...</span>
    <span class="line">...</span>
    <span class="line highlighted">...</span>
    <span class="line highlighted">...</span>
    <span class="line highlighted">...</span>
    <span class="line">...</span>

    This presents a challenge when multiple highlighted lines are siblings; we want it to *appear* like one big highlighted block. So the first ".highlighted" element should have a rounded top-right corner, and the last ".highlighted" element should have a rounded bottom-right corner.

    We also want to add some padding to the start/end of the pseudo-block, feels more comfortable.

    We can do this with a combination of ":not" and ":has". Won't have perfect browser support, but that’s fine, it just means some of the corners won't be rounded in those situations.
  */
  .highlighted:not(.highlighted + .highlighted) {
    padding-top: 2px;

    &::before {
      border-top-right-radius: var(--bump-width);
    }
  }

  .highlighted:has(+ .line:not(.highlighted)) {
    padding-bottom: 4px;

    &::before {
      border-bottom-right-radius: var(--bump-width);
    }
  }

  .highlighted:last-of-type {
    padding-bottom: 4px;

    &::before {
      border-bottom-right-radius: var(--bump-width);
    }
  }

  /*
    Finally, in addition to the highlighted annotations, we can also apply a fade-out to *unimportant* lines.
  */
  .faded {
    opacity: 0.5;
  }
`;

export default AnnotatedContainer;
